<template>
  <h3 class="text-center text-pageheadercolor">Committee Member</h3>
  <div style="max-width: 1000px" class="q-mx-auto">
    <CSCard class="full-width">
      <CSCardSection v-if="committeeMemberPaymentRequest.concurrencyRV">
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Name: </span>
          <span class="col">{{ name }}</span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Email: </span>
          <span class="col">{{
            committeeMemberPaymentRequest.emailAddress
          }}</span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px"> Mobile: </span>
          <span class="col">{{
            formatPhoneNumber(committeeMemberPaymentRequest.mobilePhoneNumber)
          }}</span>
        </div>
        <div
          class="row"
          v-for="address in committeeMemberPaymentRequest.committeeMemberAddresses"
        >
          <span class="text-weight-medium" style="width: 150px">
            {{
              mapReferenceCodeToName(
                store.state.referenceData.addressTypes,
                address.addressTypeCd,
              )
            }}:
          </span>
          <span class="col">
            {{
              formatAddress({
                street: `${address.streetLine1}${
                  address.streetLine2 ? " " + address.streetLine2 : ""
                }`,
                suburbOrCity: address.suburbOrCity,
                australianStateCd: address.australianStateCd,
                postcode: address.postcode,
                addressTypeCd: address.addressTypeCd,
              })
            }}
          </span>
        </div>
        <div class="row">
          <span class="text-weight-medium" style="width: 150px">
            Payment information:
          </span>
          <span class="col">{{
            committeeMemberPaymentRequest.currentPaymentSource
              ? "Payment information received"
              : "No Payment information received"
          }}</span>
        </div>
        <CSCardActions align="right">
          <CSButton
            label="Member Payments"
            :to="`/committeemember/paymentlist`"
            class="q-mr-sm q-my-xs"
          />
          <CSButton
            label="Edit Profile"
            :to="`/committeemember/details?sor=${committeeMemberPaymentRequest.userReference}`"
            class="q-mr-sm q-my-xs"
          />
          <CSButton
            label="Edit Account"
            :to="`/committeemember/paymentdetails?sor=${committeeMemberPaymentRequest.userReference}`"
            class="q-mr-sm q-my-xs"
          />
        </CSCardActions>
      </CSCardSection>
      <CSCardSection v-else>
        <div class="row justify-center">
          <p class="text-h5">Not registered as Committee Member</p>
        </div>
        <div class="row">
          <CSButton
            :to="`/committeemember/details?sor=${committeeMemberPaymentRequest.userReference}`"
            class="q-mx-auto"
          >
            Register
          </CSButton>
        </div>
      </CSCardSection>
    </CSCard>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive } from "vue";
import { QSpinnerGears, useQuasar } from "quasar";
import { showErrorNotification } from "@/components/shared/dialog";
import { useStore } from "@/store";
import { useRouter } from "vue-router";
import {
  formatAddress,
  formatPhoneNumber,
  mapReferenceCodeToName,
  processResponse,
} from "@/shared/helperFunctions";
import AdministrationPayerViewModel from "@/models/ViewModels/AdministrationPayerViewModel";
import CSCard from "@/components/CharitySystem/CSCard.vue";
import CSCardSection from "@/components/CharitySystem/CSCardSection.vue";
import CSButton from "@/components/CharitySystem/CSButton.vue";
import CSCardActions from "@/components/CharitySystem/CSCardActions.vue";
import CommitteeMemberUseCase from "@/usecases/CommitteeMemberUseCase";
import { ApiException, createApiException } from "@/api/ResponseModel";
import { AdministrationPayerTypeEnum } from "@/shared/ModelEnums";
import { AdministrationPayer_Request } from "@/models/APIModels/AdministrationPayer";
import { UserViewModel } from "@/models/ViewModels/UserModel";
import UserUsecase from "@/usecases/UserUsecase";
import { computed } from "vue";

const userUseCase = new UserUsecase();

const committeeMemberPaymentRequest = reactive<AdministrationPayer_Request>(
  userUseCase.convertUserToAdministratiorRequest(
    new UserViewModel(),
    AdministrationPayerTypeEnum.APT_CM,
  ),
);

const name = computed(
  () =>
    `${committeeMemberPaymentRequest.givenName} ${committeeMemberPaymentRequest.familyName}`,
);
const $q = useQuasar();
const store = useStore();
const router = useRouter();

onMounted(async () => {
  try {
    // Assumption only school officers can be committee members
    const userReference = store.state.user?.userReference;

    if (userReference) {
      const committeeMemberUseCase = new CommitteeMemberUseCase();
      const result = await committeeMemberUseCase.selectUser(userReference);
      processResponse(result, (content) => {
        const returnedCM = userUseCase.convertUserToAdministratiorRequest(
          content,
          AdministrationPayerTypeEnum.APT_CM,
        );
        if (returnedCM)
          Object.assign(committeeMemberPaymentRequest, returnedCM);
      });
    } else {
      router.go(-1);
      throw createApiException(
        "User not logged in",
        "You must be logged in to view Committee Member details.",
      );
    }
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  }
});

async function onMakePayment() {
  $q.dialog({
    title: "Commence Payment",
    message:
      "Payment will now be made from your account. Press Ok to continue.",
    ok: true,
    cancel: true,
    persistent: true,
  }).onOk(await makePayment);
}

async function makePayment() {
  try {
    $q.loading.show({
      message:
        "Your Committee Membership payment is now being processed. Please wait...",
      boxClass: "bg-grey-2 text-grey-9",
      spinnerColor: "primary",
      spinner: QSpinnerGears,
    });
    const committeeMemberUseCase = new CommitteeMemberUseCase();
    const result = await committeeMemberUseCase.chargeAccount(
      committeeMemberPaymentRequest.userReference,
    );
    processResponse(result, (content) => {
      switch (content.paymentStatusCd) {
        case "PS_P":
        case "PS_S":
          const message =
            content.paymentStatusCd === "PS_P"
              ? "Your payment is being processed. This will not hold up your registration and we will send you a receipt once your payment is approved. Thank you for being part of the Help A Family community."
              : "Your payment has been successful and your payment receipt has been emailed to you. Thank you for being part of the Help A Family community.";
          $q.dialog({
            title:
              content.paymentStatusCd === "PS_P"
                ? "Payment Processing"
                : "Payment Successful",
            message: message,
            ok: true,
            cancel: false,
            persistent: true,
          });
          break;
        case "PS_U":
        case "PS_C":
          const failMessage =
            content.paymentStatusCd === "PS_U"
              ? `Unfortunately your payment was unsuccessful. Our payment provider has provided the following information: "${content.error}". This will not hold up your registration but please provide payment as soon as possible. Would you like to check your account details and retry payment now?`
              : "Unfortunately your payment was cancelled. This will not hold up your registration but please provide payment as soon as possible. Would you like to check your account details and retry payment now?";
          $q.dialog({
            title:
              content.paymentStatusCd === "PS_U"
                ? "Payment Unsuccessful"
                : "Payment Cancelled",
            message: failMessage,
            ok: { push: true, label: "Yes", flat: true },
            cancel: { push: true, label: "No" },
            persistent: true,
          }).onOk(() =>
            router.push(
              `/committeemember/paymentdetails?sor=${committeeMemberPaymentRequest.userReference}`,
            ),
          );
          break;
        default:
          throw createApiException(
            "Payment Status Error",
            "The payment status returned is not recognized. Please try again later.",
          );
      }
    });
  } catch (ex) {
    $q.notify(showErrorNotification((ex as ApiException).title));
  } finally {
    $q.loading.hide();
  }
}
</script>
